import { SvgConsumer } from '@components/icons/svgConsumer';
import { Button } from '@components/ui/button';
import { cn } from '@utils/cn';
import { DragEvent, RefObject, SyntheticEvent, forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileInputProps } from './types';
import { useToast } from '@hooks/useToast';
import { FolderUp } from 'lucide-react';
import { MAX_FILES_PER_UPLOAD } from './constants';
import { getDroppedEntries, isSystemFile, readDroppedEntries } from './helpers';

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      files,
      setFiles,
      removeFile,
      dragAndDrop,
      accept,
      hideFiles,
      maxLength,
      withCameraAccess,
      allowFolders,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation('app');
    const { toast } = useToast();

    const inputRef = ref as RefObject<HTMLInputElement | null>;
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const folderInputRef = useRef<HTMLInputElement>(null);
    const maxLengthInBytes = maxLength ? maxLength * 1024 ** 2 : 0;

    const showError = (description: string) =>
      toast({ title: t('error'), description, variant: 'destructive' });

    const addValidFiles = (fileList: FileList | File[], fromFolder = false) => {
      const current = files ? Array.from(files) : [];
      const incoming = Array.from(fileList).filter((file) => !fromFolder || !isSystemFile(file));
      if (fromFolder && !incoming.length) return showError(t('messages.failure.file.emptyFolder'));
      const tooLarge = incoming.filter((file) => maxLengthInBytes && file.size > maxLengthInBytes);
      const valid = incoming.filter((file) => !tooLarge.includes(file));
      const room = Math.max(MAX_FILES_PER_UPLOAD - current.length, 0);
      if (tooLarge.length === 1)
        showError(t('messages.failure.file.size', { name: tooLarge[0].name }));
      else if (tooLarge.length > 1)
        showError(t('messages.failure.file.sizeMany', { count: tooLarge.length, max: maxLength }));
      if (valid.length > room)
        showError(t('messages.failure.file.limit', { max: MAX_FILES_PER_UPLOAD }));
      const dt = new DataTransfer();
      current.concat(valid.slice(0, room)).forEach((file) => dt.items.add(file));
      setFiles(dt.files);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const entries = getDroppedEntries(e.dataTransfer);
      if (!entries.length) return addValidFiles(e.dataTransfer.files);
      const hasFolder = entries.some((entry) => entry.isDirectory);
      if (hasFolder && !allowFolders) showError(t('messages.failure.file.folderNotAllowed'));
      const accepted = allowFolders ? entries : entries.filter((entry) => entry.isFile);
      if (!accepted.length) return;
      void readDroppedEntries(accepted).then((dropped) => addValidFiles(dropped, hasFolder));
    };

    if (!inputRef) return null;

    return (
      <div className="flex flex-col gap-2">
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setIsDragging(false)}
          onDragEnter={() => setIsDragging(true)}
          className={cn([
            'relative flex h-40 w-full flex-col items-center justify-center gap-3 border border-dashed border-neutral-800 font-semibold text-neutral-500 after:absolute after:left-0 after:top-0 after:h-full after:w-full',
            isDragging
              ? 'border-primary-800 bg-primary-50 text-primary-700 after:block'
              : 'bg-neutral-50 after:hidden',
          ])}>
          {isDragging ? (
            <span>{t('fileUpload.drop')}</span>
          ) : (
            <>
              <span className="text-sm">
                {t(
                  `fileUpload.${dragAndDrop ? (allowFolders ? 'dragAndDropFolders' : 'dragAndDrop') : withCameraAccess ? 'chooseFilesOrTakePhotos' : 'chooseFiles'}`,
                )}
              </span>
              {dragAndDrop && <span className="text-xs">{t('fileUpload.or')}</span>}
              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="secondary" onClick={() => inputRef?.current?.click()}>
                  <SvgConsumer id="uploadDocument" />
                  {withCameraAccess
                    ? t('fileUpload.browseFilesOrTakePhotos')
                    : t('fileUpload.browseFiles')}
                </Button>
                {allowFolders && (
                  <Button variant="secondary" onClick={() => folderInputRef.current?.click()}>
                    <FolderUp className="h-4 w-4" />
                    {t('fileUpload.browseFolder')}
                  </Button>
                )}
              </div>
            </>
          )}

          <input
            ref={ref}
            className="hidden"
            onChange={(e: SyntheticEvent<HTMLInputElement>) => {
              if (!e.currentTarget.files) return;
              addValidFiles(e.currentTarget.files);
            }}
            type="file"
            accept="*"
            maxLength={maxLengthInBytes}
            capture={accept?.includes('image') ? 'environment' : undefined}
            {...rest}
          />
          {allowFolders && (
            <input
              ref={(el) => {
                el?.setAttribute('webkitdirectory', '');
                folderInputRef.current = el;
              }}
              className="hidden"
              type="file"
              multiple
              onChange={(e: SyntheticEvent<HTMLInputElement>) => {
                const input = e.currentTarget;
                if (!input.files) return;
                addValidFiles(input.files, true);
                input.value = '';
              }}
            />
          )}
        </div>
        {!hideFiles && files && Boolean(Array.from(files).length) && (
          <>
            <h3 className="text-m font-bold text-neutral-500">{t('fileUpload.selectedFiles')}</h3>
            <div className="flex flex-col gap-2">
              {Array.from(files).map((file, index) => (
                <div
                  className="flex items-center justify-between gap-2 text-sm"
                  key={[file.name, index].join('-')}>
                  <span className="w-[200px] overflow-hidden text-ellipsis font-bold text-primary-500">
                    {file.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap text-neutral-400">
                      {file.size
                        ? Math.round(((file.size + Number.EPSILON) / 1024 / 1024) * 100) / 100
                        : 0}{' '}
                      MB
                    </span>
                    <Button
                      className="-mr-2 mb-[1px] h-fit p-2"
                      variant="ghost"
                      onClick={() => removeFile(index)}>
                      <SvgConsumer className="text-neutral-300" id="trash" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  },
);
